import React, { FC, PropsWithChildren, useState, useReducer } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { Box } from '@dxb-web-ui-framework/design-system';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { accountService } from '../../../../../channels/secure-web/services';
import {
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { ECOMMBXLoader } from '../../../../content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { AccountList } from '../../domain/Account/entities';
import { AccountListContext, AccountListDispatchContext } from './AccountListContext';
import { defaultAccountListState } from './defaultState';
import { AccountListReducer } from './AccountListReducer';
import { AccountListProviderProps } from './types';

const AccountListProvider: FC<PropsWithChildren<AccountListProviderProps>> = ({ children }) => {
  // Get initial state values
  const [timestamp] = useState(new Date().toISOString());
  const [state, dispatch] = useReducer(AccountListReducer, defaultAccountListState);
  const customerId = useSelectedCustomerId();
  const accountsIsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.DepositAccounts);

  const shouldFetchAccountList = !isEmpty(customerId) && accountsIsAllowed;
  const {
    data: accountsData,
    error: accountsDataError,
    isLoading: isLoadingAccounts,
    mutate: accountsDataMutator,
  } = useInvokeSWRService<AccountList>(
    accountService.GetCustomerAccounts,
    {
      customerId,
      retrieveSummary: true,
      meta: {
        timestamp,
      },
    },
    shouldFetchAccountList,
  );

  const onAccountListRefetch = () => {
    accountsDataMutator && accountsDataMutator();
  };

  return (
    <AccountListContext.Provider
      value={{
        ...state,
        accountListData: accountsData,
        onAccountListRefetch,
        loadingState: {
          isLoadingAccounts,
        },
        errorState: {
          accountsDataError,
        },
      }}>
      <AccountListDispatchContext.Provider value={dispatch}>
        {isLoadingAccounts && (
          <Box style={{ height: '100vh', position: 'absolute', top: '50%', right: '50%' }}>
            <ECOMMBXLoader size='full' verticalSpacing='large' />
          </Box>
        )}
        {accountsData && !isLoadingAccounts && children}
      </AccountListDispatchContext.Provider>
    </AccountListContext.Provider>
  );
};

export { AccountListProvider };
